import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  IPriceLine,
  LineStyle,
  UTCTimestamp,
} from 'lightweight-charts';
import { cgGet, cgErrorMsg, CG_MAX_DAYS } from '../../utils/coingecko';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useDatedDisplayRates } from '../../hooks/useDatedDisplayRates';
import { PerUsdOn } from '../../utils/displaySeries';
import { formatCompactMoney, formatMarketPrice } from '../../utils/formatters';
import {
  CandleInterval,
  FOUR_HOURS_MS,
  HOUR_MS,
  OhlcBar,
  aggregateTo4h,
  convertBars,
  coingeckoVsCurrency,
  createSessionCache,
  isIntraday,
  localWallClockSec,
  medianStepMs,
  normalizeInterval,
  parseOhlcRows,
  chainOpens,
  withLiveCandle,
  MAX_LIVE_LAG_PERIODS,
  toLocalChartBars,
} from '../../utils/intradayCandles';
import {
  DAILY_OHLC_DAYS,
  parseDailyOhlc,
  mergeDailyCandles,
  weeklyFromDaily,
  matchVolumes,
} from '../../utils/dailyCandles';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ChartView = 'line' | 'candle';

/** Candle interval. User-facing labels: 1H / 4H / 1D / 1W. */
export type { CandleInterval };

interface Candle {
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface VolBar {
  time: UTCTimestamp;
  value: number;
  color: string;
}

interface LegendState {
  open: number;
  high: number;
  low: number;
  close: number;
  vol: number;
  visible: boolean;
  isCandle: boolean; // true when hovering a candle (has a real open/close pair)
}

export interface PriceLineOverlay {
  price: number;       // in the DISPLAY currency (the chart's price scale)
  color: string;       // hex
  title: string;       // e.g. "Buy T1"
}

interface Props {
  /**
   * The live price in the display currency, exactly as the page header shows it.
   * The newest candle closes on it (see pinLastClose) so the two always agree.
   */
  livePrice?: number;
  coinId: string;
  interval: CandleInterval;
  view: ChartView;
  priceLines?: PriceLineOverlay[];
}

// ─── Chart theme ───────────────────────────────────────────────────────────────

function makeChartOpts(isDark: boolean) {
  const crosshairColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)';
  const labelBg        = isDark ? '#2a2a2a' : '#e8e8e8';
  return {
    layout: {
      background: { color: isDark ? '#161616' : '#ffffff' },
      textColor:  isDark ? '#888888' : '#555555',
      fontSize:   11,
    },
    grid: {
      vertLines: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
      horzLines: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
    },
    crosshair: {
      vertLine: { color: crosshairColor, labelBackgroundColor: labelBg },
      horzLine: { color: crosshairColor, labelBackgroundColor: labelBg },
    },
    rightPriceScale: { borderColor: 'transparent' },
    timeScale:       { borderColor: 'transparent', timeVisible: true, secondsVisible: false },
    // Native pan/zoom — wheel zoom, pinch, and drag-to-scroll across loaded history.
    handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
    handleScale:  { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
  };
}

// ─── Series options ────────────────────────────────────────────────────────────

const CANDLE_OPTS = {
  upColor:         '#6db981',
  downColor:       '#c4615e',
  borderUpColor:   '#6db981',
  borderDownColor: '#c4615e',
  wickUpColor:     '#6db981',
  wickDownColor:   '#c4615e',
  priceLineVisible: false,
  lastValueVisible: true,
} as const;

const LINE_OPTS = {
  color:            '#6db981',
  lineWidth:        2 as const,
  priceLineVisible: false,
  lastValueVisible: true,
  visible:          false,
} as const;

const VOL_OPTS = {
  priceFormat:      { type: 'volume' as const },
  priceScaleId:     'vol',
  priceLineVisible: false,
  lastValueVisible: false,
} as const;

// ─── Data fetching ─────────────────────────────────────────────────────────────

// 1D and 1W are aggregated from ~2 years of daily market_chart data, with the
// newest 180 days replaced by real daily OHLC (fetched alongside it, once per
// coin; see utils/dailyCandles). 1H and 4H come from a separate hourly OHLC fetch, made only when an
// intraday interval is first opened (see fetchIntraday).
const CHART_TIMEOUT = 20_000;

// Each interval opens on its most recent candles: 3 days of 1H, 2 weeks of 4H,
// 2 months of 1D, a little over a year of 1W. Everything loaded stays available
// to zoom and scroll out to.
/** Length of one candle, in seconds, for deciding whether the newest is current. */
const PERIOD_SEC: Record<CandleInterval, number> = {
  hourly: 3_600,
  '4h':   14_400,
  daily:  86_400,
  weekly: 604_800,
};

const INITIAL_VISIBLE: Record<CandleInterval, number> = {
  hourly: 72,
  '4h':   84,
  daily:  60,
  weekly: 60,
};

const UP_VOL   = 'rgba(38,166,154,0.5)';
const DOWN_VOL = 'rgba(239,83,80,0.5)';

const DAY_MS  = 86_400_000;
const DAY_SEC = 86_400;

/** UTC-midnight day-start (in seconds) for a millisecond timestamp. */
function dayStartSec(ms: number): number {
  return Math.floor(ms / DAY_MS) * DAY_SEC;
}

/** Monday-00:00-UTC week start (in seconds) for a millisecond timestamp. */
function weekStartSec(ms: number): number {
  const dayMs  = Math.floor(ms / DAY_MS) * DAY_MS;         // UTC midnight
  const dowMon = (new Date(dayMs).getUTCDay() + 6) % 7;    // 0=Mon … 6=Sun
  return Math.floor((dayMs - dowMon * DAY_MS) / 1000);
}

type IntervalData = { candles: Candle[]; volumes: VolBar[] };

/**
 * Aggregate raw daily market_chart points into OHLC candles for one bucket size
 * (day / week). Candles are gapless like TradingView / CoinMarketCap:
 * each candle's OPEN is the PREVIOUS candle's close, and high/low span the open,
 * the close, and any intra-bucket extremes from the daily points. Volume is the
 * per-bucket sum.
 */
function aggregateCandles(
  prices: [number, number][],
  rawVols: [number, number][],
  bucketOf: (ms: number) => number,
): IntervalData {
  type Bucket = { open: number; close: number; hi: number; lo: number; vol: number };
  const map = new Map<number, Bucket>();

  const ordered = [...prices].sort((a, b) => a[0] - b[0]);
  for (const [ts, price] of ordered) {
    const key = bucketOf(ts);
    const b   = map.get(key);
    if (!b) map.set(key, { open: price, close: price, hi: price, lo: price, vol: 0 });
    else  { b.hi = Math.max(b.hi, price); b.lo = Math.min(b.lo, price); b.close = price; }
  }
  for (const [ts, vol] of rawVols) {
    const b = map.get(bucketOf(ts));
    if (b) b.vol += vol;
  }

  const sorted = Array.from(map.entries()).sort(([a], [b]) => a - b);

  const candles: Candle[] = [];
  let prevClose: number | null = null;
  for (const [sec, b] of sorted) {
    const open  = prevClose ?? b.open;        // gapless: open = previous close
    const close = b.close;
    candles.push({
      time: sec as UTCTimestamp,
      open,
      high: Math.max(b.hi, open, close),
      low:  Math.min(b.lo, open, close),
      close,
    });
    prevClose = close;
  }

  const volumes: VolBar[] = sorted.map(([sec, b], i) => ({
    time:  sec as UTCTimestamp,
    value: b.vol,
    color: candles[i].close >= candles[i].open ? UP_VOL : DOWN_VOL,
  }));

  return { candles, volumes };
}

// ─── Which currency to fetch in ────────────────────────────────────────────────
//
// Candles are fetched IN THE DISPLAY CURRENCY whenever CoinGecko supports it
// (every currency the app offers), and then used as-is: no FX conversion. That
// is what removes the steps: converting USD candles per day stitched together
// rate sources (the RBA bundle, then ECB, then spot) and the hand-over between
// them showed as a jump at one day boundary. CoinGecko's own AUD series is
// already continuous. Only a currency CoinGecko lacks falls back to USD candles
// converted per day (buildIntervals / intradayCandles with real rates).
//
// Data is cached per coin AND currency for the session: switching currency
// fetches once for the new one, and switching back is free.

/** "bitcoin|aud". */
const dataKey = (coinId: string, vs: string) => `${coinId}|${vs}`;
const keyParts = (key: string) => {
  const i = key.lastIndexOf('|');
  return { coinId: key.slice(0, i), vs: key.slice(i + 1) };
};

/** No conversion: the candles are already in the display currency. */
const NO_CONVERSION: PerUsdOn = { perUsdOn: () => 1 };

/** The coin's raw daily market_chart, exactly as CoinGecko returned it in `vs`. */
interface RawChart {
  key: string;
  prices: [number, number][];
  vols: [number, number][];
  /** Real daily OHLC for the newest DAILY_OHLC_DAYS, or null if it could not be had. */
  ohlc: OhlcBar[] | null;
}

/**
 * Real daily OHLC (paid plans, up to 180 days). Never throws: any failure, or a
 * response that is not daily (4-day candles on a plan without the interval),
 * is null and 1D falls back to the market_chart candles without wicks.
 */
async function loadDailyOhlc(coinId: string, vs: string): Promise<OhlcBar[] | null> {
  try {
    const res = await cgGet(`/coins/${coinId}/ohlc`, {
      params: { vs_currency: vs, days: DAILY_OHLC_DAYS, interval: 'daily' },
      timeout: CHART_TIMEOUT,
    });
    const bars = parseDailyOhlc(res.data);
    if (!bars) console.info(`[MyCryptoPlanner] ${coinId}: no daily OHLC, 1D candles have no wicks`);
    return bars;
  } catch {
    return null;
  }
}

/**
 * Fetch the coin's ~2-year daily market_chart and its daily OHLC ONCE per
 * currency, in parallel. market_chart gives the history and the volume; the
 * OHLC gives the newest days their real highs and lows.
 */
async function loadRawChart(key: string): Promise<RawChart> {
  const { coinId, vs } = keyParts(key);
  const [res, ohlc] = await Promise.all([
    cgGet(`/coins/${coinId}/market_chart`, {
      params: { vs_currency: vs, days: CG_MAX_DAYS },
      timeout: CHART_TIMEOUT,
    }),
    loadDailyOhlc(coinId, vs),
  ]);
  return {
    key,
    prices: ((res.data as any).prices ?? []) as [number, number][],
    vols:   ((res.data as any).total_volumes ?? []) as [number, number][],
    ohlc,
  };
}

const dailyCache = createSessionCache(loadRawChart);

const utcDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * Daily / weekly candle sets, plus the daily line, in the DISPLAY
 * currency. Each daily point is converted at its own day's rate BEFORE
 * aggregation, so a weekly candle's high is the highest display-currency price
 * that week, not a USD high converted afterwards at some other day's rate.
 */
function buildIntervals(raw: RawChart, rates: PerUsdOn): Record<'daily' | 'weekly', IntervalData> {
  const conv = (pts: [number, number][]) =>
    pts.map(([ts, v]) => [ts, v * rates.perUsdOn(utcDay(ts))] as [number, number]);
  const prices = conv(raw.prices);
  const vols   = conv(raw.vols);
  const daily  = aggregateCandles(prices, vols, dayStartSec);
  const weekly = aggregateCandles(prices, vols, weekStartSec);
  if (!raw.ohlc) return { daily, weekly };

  // Real daily OHLC for the days it covers, market_chart candles for the rest;
  // weeks rebuilt from those days so their wicks are true highs and lows too.
  // Volumes stay market_chart's, matched to the candles by day / week.
  const ohlc: Candle[] = convertBars(raw.ohlc, rates).map((b) => ({
    time: (b.openMs / 1000) as UTCTimestamp, open: b.open, high: b.high, low: b.low, close: b.close,
  }));
  const dailyCandles  = mergeDailyCandles(daily.candles, ohlc);
  const weeklyCandles = weeklyFromDaily(dailyCandles, weekStartSec);
  return {
    daily:  { candles: dailyCandles,  volumes: matchVolumes(dailyCandles,  daily.volumes,  UP_VOL, DOWN_VOL) as VolBar[] },
    weekly: { candles: weeklyCandles, volumes: matchVolumes(weeklyCandles, weekly.volumes, UP_VOL, DOWN_VOL) as VolBar[] },
  };
}

// ─── Intraday (1H / 4H) ────────────────────────────────────────────────────────

/** The coin's intraday candles in the fetched currency. `hourly` is null when only 4H could be had. */
interface IntradayData {
  key: string;
  hourly: OhlcBar[] | null;
  fourHour: OhlcBar[];
}

/**
 * Hourly OHLC (real candles, ~90 days, paid plans) and 4H built from it. If the
 * hourly request fails or comes back coarser than hourly, one fallback request
 * for CoinGecko's own 4-hour candles (30 days) keeps 4H working; 1H then reports
 * itself unavailable. See utils/intradayCandles for why /ohlc and not market_chart.
 */
async function loadIntraday(key: string): Promise<IntradayData> {
  const { coinId, vs } = keyParts(key);
  try {
    const res = await cgGet(`/coins/${coinId}/ohlc`, {
      params: { vs_currency: vs, days: 90, interval: 'hourly' },
      timeout: CHART_TIMEOUT,
    });
    const rows = res.data as number[][];
    if (Array.isArray(rows) && rows.length > 1 && medianStepMs(rows) <= HOUR_MS * 1.1) {
      const hourly = parseOhlcRows(rows, HOUR_MS);
      if (hourly.length > 0) return { key, hourly, fourHour: aggregateTo4h(hourly) };
    }
  } catch {
    /* no hourly access, or a failure: try the 4-hour candles below */
  }
  const res = await cgGet(`/coins/${coinId}/ohlc`, {
    params: { vs_currency: vs, days: 30 },
    timeout: CHART_TIMEOUT,
  });
  const rows = res.data as number[][];
  if (!Array.isArray(rows) || rows.length < 2 || medianStepMs(rows) > FOUR_HOURS_MS * 1.1) {
    throw new Error('Intraday candles unavailable');
  }
  return { key, hourly: null, fourHour: parseOhlcRows(rows, FOUR_HOURS_MS) };
}

// One intraday fetch per coin and currency per session: the promise is cached
// (so concurrent opens share it, too) and only an explicit Retry, or a failure,
// clears it.
const intradayCache = createSessionCache(loadIntraday);

const HOURLY_UNAVAILABLE =
  'Hourly candles are not available from the price feed right now. 4H, 1D and 1W still work.';

/** Display-currency candles (converted only on the fallback path), in local
 *  wall-clock time so the axis reads in hours. */
function intradayCandles(bars: readonly OhlcBar[], rates: PerUsdOn): Candle[] {
  return toLocalChartBars(convertBars(bars, rates)).map((b) => ({
    time: b.time as UTCTimestamp,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
  }));
}

// ─── Formatters ────────────────────────────────────────────────────────────────

/** Legend price, already in `code`. Same precision steps as before, plus the symbol. */
function fmtP(n: number, code: string): string {
  if (!n) return '—';
  const opts: Intl.NumberFormatOptions =
    n >= 10_000 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : n >= 1_000 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : n >= 1     ? { minimumFractionDigits: 4, maximumFractionDigits: 4 }
    :              { minimumSignificantDigits: 5, maximumSignificantDigits: 5 };
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, ...opts }).format(n);
  } catch {
    return n.toPrecision(5);
  }
}

/** Legend volume, already in `code` (A$1.2B). */
function fmtV(n: number, code: string): string {
  if (!n) return '—';
  return formatCompactMoney(n, code);
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function TradingChart({ coinId, interval: intervalProp, view, priceLines, livePrice }: Props) {
  // Anything unsupported (including a stale 'monthly') renders as 1D.
  const interval = normalizeInterval(intervalProp);
  const intraday = isIntraday(interval);
  const { theme }    = useTheme();
  const isDark       = theme === 'dark';
  const { currency } = useCurrency();
  // Fetch in the display currency when CoinGecko has it (always, for the app's
  // currencies); otherwise USD, converted per day below.
  const nativeVs = coingeckoVsCurrency(currency.code);
  const vs = nativeVs ?? 'usd';
  const needsConversion = !nativeVs;
  const key = dataKey(coinId, vs);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const candleRef    = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lineRef      = useRef<ISeriesApi<'Line'> | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const volRef       = useRef<ISeriesApi<'Histogram'> | null>(null);
  const priceLinesRef = useRef<IPriceLine[]>([]);

  // The raw USD market_chart, fetched once per coin. Switching interval or display
  // currency rebuilds candles from it (no refetch).
  const [raw, setRaw] = useState<RawChart | null>(null);
  // Which coin+interval the visible range was last set for, e.g. "bitcoin:weekly".
  // A currency or rate change re-plots without resetting the user's zoom/scroll.
  const viewKeyRef = useRef<string | null>(null);
  // What is currently in the series, so a live-price tick can update one bar in
  // place instead of resetting the whole dataset.
  const appliedRef = useRef<{ base: Candle[]; volumes: VolBar[]; length: number; lastTime: number | null } | null>(null);
  // The coin + interval the "feed is stale" warning was last given for.
  const staleWarnedRef = useRef<string | null>(null);

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  // Intraday data, fetched lazily the first time 1H or 4H is opened for a coin.
  // Keyed by coin AND currency (dataKey), like the caches.
  const [intra, setIntra] = useState<{
    key: string; loading: boolean; data?: IntradayData; error?: string;
  } | null>(null);
  const [intraRetry, setIntraRetry] = useState(0);
  const handledIntraRetryRef = useRef(0);
  const [legend, setLegend]         = useState<LegendState>({
    open: 0, high: 0, low: 0, close: 0, vol: 0, visible: false, isCandle: false,
  });

  // ── Mount: create chart and all series once ────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const initDark = document.documentElement.classList.contains('dark');
    const chart = createChart(containerRef.current, {
      ...makeChartOpts(initDark),
      width:  containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, { ...CANDLE_OPTS, visible: true });
    const lineSeries   = chart.addSeries(LineSeries,        { ...LINE_OPTS,   visible: false });
    const volSeries    = chart.addSeries(HistogramSeries,   VOL_OPTS);

    // Candles use top 78%, volume uses bottom 22%
    chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: 0.23 } });
    chart.priceScale('vol').applyOptions(  { scaleMargins: { top: 0.78, bottom: 0    } });

    // Crosshair legend
    chart.subscribeCrosshairMove((params) => {
      if (!params.time) { setLegend((p) => ({ ...p, visible: false })); return; }

      const cd = params.seriesData.get(candleSeries);
      const ld = params.seriesData.get(lineSeries);
      const vd = params.seriesData.get(volSeries);
      const vol = (vd && 'value' in vd) ? (vd as { value: number }).value : 0;

      if (cd && 'open' in cd) {
        const c = cd as { open: number; high: number; low: number; close: number };
        setLegend({ open: c.open, high: c.high, low: c.low, close: c.close, vol, visible: true, isCandle: true });
      } else if (ld && 'value' in ld) {
        const v = (ld as { value: number }).value;
        setLegend({ open: v, high: v, low: v, close: v, vol, visible: true, isCandle: false });
      }
    });

    // Resize observer
    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.applyOptions({
        width:  containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    });
    ro.observe(containerRef.current);

    chartRef.current  = chart;
    candleRef.current = candleSeries;
    lineRef.current   = lineSeries;
    volRef.current    = volSeries;

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current  = null;
      candleRef.current = null;
      lineRef.current   = null;
      volRef.current    = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply theme on change ──────────────────────────────────────────────────
  useEffect(() => {
    chartRef.current?.applyOptions(makeChartOpts(isDark));
  }, [isDark]);

  // ── Toggle series visibility on view change ────────────────────────────────
  useEffect(() => {
    candleRef.current?.applyOptions({ visible: view === 'candle' });
    lineRef.current?.applyOptions(  { visible: view === 'line'   });
  }, [view]);

  // ── Sync overlay price lines on the visible series ────────────────────────
  useEffect(() => {
    const series = view === 'candle' ? candleRef.current : lineRef.current;
    if (!series) return;

    // Tear down old lines on whichever series owned them
    priceLinesRef.current.forEach((pl) => {
      try { candleRef.current?.removePriceLine(pl); } catch { /* not ours */ }
      try { lineRef.current?.removePriceLine(pl);   } catch { /* not ours */ }
    });
    priceLinesRef.current = [];

    if (!priceLines || priceLines.length === 0) return;

    priceLines
      .filter(p => p.price > 0 && isFinite(p.price))
      .forEach((p) => {
        const line = series.createPriceLine({
          price:             p.price,
          color:             p.color,
          lineWidth:         1,
          lineStyle:         LineStyle.Dashed,
          axisLabelVisible:  true,
          title:             p.title,
        });
        priceLinesRef.current.push(line);
      });
  }, [priceLines, view]);

  // Open at a comfortable zoom: the interval's most recent candles (thick, well
  // spaced). The full dataset stays loaded, so the user can still zoom/scroll out.
  const setInitialView = useCallback((candles: Candle[], visible: number) => {
    const ts = chartRef.current?.timeScale();
    if (!ts || candles.length === 0) return;
    const from = Math.max(0, candles.length - visible);
    try { ts.setVisibleLogicalRange({ from, to: candles.length - 1 }); }
    catch { ts.fitContent(); }
  }, []);

  // Both series share the SAME interval timestamps: the line follows the selected
  // interval's candle closes, so Lightweight Charts builds one matching time scale
  // (no gaps between candles, sensible period axis labels).
  const applyDataset = useCallback(
    (candles: Candle[], volumes: VolBar[]) => {
      candleRef.current?.setData(candles);
      lineRef.current?.setData(candles.map((c) => ({ time: c.time, value: c.close })));
      volRef.current?.setData(volumes);
    },
    [],
  );

  // ── Fetch the daily series (once per coin and currency) ────────────────────
  const handledRetryRef = useRef(0);
  useEffect(() => {
    const fresh = retryCount !== handledRetryRef.current;
    handledRetryRef.current = retryCount;
    if (raw?.key === key && !fresh) return;
    let cancelled = false;
    setError(null);
    // Cached for this currency: no overlay flash, the promise resolves at once.
    if (!dailyCache.has(key) || fresh) setLoading(true);
    setLegend((p) => ({ ...p, visible: false }));
    dailyCache.get(key, fresh)
      .then((r) => { if (!cancelled) setRaw(r); })
      .catch((err) => {
        if (cancelled) return;
        console.error('[MyCryptoPlanner] Chart fetch failed:', err);
        setError(cgErrorMsg(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retryCount]);

  // ── Fetch intraday candles (lazily; once per coin and currency) ────────────
  useEffect(() => {
    if (!intraday) return;
    const fresh = intraRetry !== handledIntraRetryRef.current;
    handledIntraRetryRef.current = intraRetry;
    let cancelled = false;
    // Already have this coin's data: keep showing it while the (cached, instant)
    // promise resolves, instead of flashing the loading overlay.
    setIntra((prev) => (prev?.key === key && prev.data && !fresh ? prev : { key, loading: true }));
    intradayCache.get(key, fresh)
      .then((data) => { if (!cancelled) setIntra({ key, loading: false, data }); })
      .catch((err) => {
        if (cancelled) return;
        console.error('[MyCryptoPlanner] Intraday chart fetch failed:', err);
        setIntra({ key, loading: false, error: cgErrorMsg(err) });
      });
    return () => { cancelled = true; };
  }, [intraday, key, intraRetry]);

  const intraData = intra?.key === key ? intra.data : undefined;

  // ── Conversion: none for a natively fetched currency ───────────────────────
  // Rates are only requested (and the ECB cache only warmed) on the fallback
  // path, so a native currency costs no FX requests at all.
  const rawDates = useMemo(() => {
    if (!needsConversion) return [] as string[];
    const days = new Set<string>();
    raw?.prices.forEach(([ts]) => days.add(utcDay(ts)));
    (intraData?.hourly ?? intraData?.fourHour)?.forEach((b) => days.add(utcDay(b.openMs)));
    return Array.from(days).sort();
  }, [raw, intraData, needsConversion]);
  const datedRates = useDatedDisplayRates(rawDates);
  const rates = needsConversion ? datedRates : NO_CONVERSION;
  const intervals = useMemo(
    () => (raw && raw.key === key ? buildIntervals(raw, rates) : null),
    [raw, rates, key]
  );
  const intradaySets = useMemo(
    () => intraData
      ? {
          hourly: intraData.hourly ? intradayCandles(intraData.hourly, rates) : null,
          '4h': intradayCandles(intraData.fourHour, rates),
        }
      : null,
    [intraData, rates]
  );

  // ── Load into the series ───────────────────────────────────────────────────
  useEffect(() => {
    if (!candleRef.current) return;
    let sel: IntervalData | null = null;
    if (intraday) {
      // No volume in OHLC data, so intraday views have no volume bars.
      const set = intradaySets?.[interval as 'hourly' | '4h'];
      if (set) sel = { candles: set, volumes: [] };
    } else if (intervals) {
      sel = intervals[interval as 'daily' | 'weekly'];
    }
    if (!sel) return;
    // Display steps, after currency handling and aggregation:
    //   1. chainOpens: each candle opens at the previous close (no gaps).
    //   2. withLiveCandle: the newest candle is live. Pinned to the header's price
    //      if it is the current period; otherwise a forming candle is appended
    //      for the current period (CoinGecko lags). Same time basis as the
    //      candles: local wall clock for intraday, UTC for daily / weekly.
    // The forming candle has no volume bar: there is no volume for it yet.
    const period = PERIOD_SEC[interval];
    const nowSec = intraday ? localWallClockSec(Date.now()) : Date.now() / 1000;
    const candles = withLiveCandle(chainOpens(sel.candles), livePrice, period, nowSec);
    const newest = sel.candles[sel.candles.length - 1];
    const viewKey = `${coinId}:${interval}`;
    if (newest && nowSec - newest.time >= (MAX_LIVE_LAG_PERIODS + 1) * period && staleWarnedRef.current !== viewKey) {
      // The feed stopped well short of now, so no live candle is drawn (it would
      // join today's price onto old data). Said once, so it is visible.
      staleWarnedRef.current = viewKey;
      console.warn(
        `[MyCryptoPlanner] ${coinId} ${interval}: newest candle is ${new Date(newest.time * 1000).toISOString()} (chart time), not current`
      );
    }

    // Only the live price moved (same data, same newest bar): update that bar in
    // place, which leaves the user's zoom and scroll exactly where they are.
    // Anything else, including a forming candle rolling into a new period, is a
    // full setData, so no stale bar is left behind.
    const newestOut = candles[candles.length - 1];
    const prev = appliedRef.current;
    if (
      prev && newestOut &&
      prev.base === sel.candles && prev.volumes === sel.volumes &&
      prev.length === candles.length && prev.lastTime === newestOut.time
    ) {
      candleRef.current.update(newestOut);
      lineRef.current?.update({ time: newestOut.time, value: newestOut.close });
    } else {
      applyDataset(candles, sel.volumes);
    }
    appliedRef.current = { base: sel.candles, volumes: sel.volumes, length: candles.length, lastTime: newestOut?.time ?? null };
    sel = { candles, volumes: sel.volumes };
    // Without volume bars, let the candles use the space the bars would take.
    chartRef.current?.priceScale('right').applyOptions({
      scaleMargins: { top: 0.05, bottom: sel.volumes.length ? 0.23 : 0.05 },
    });
    if (viewKeyRef.current !== viewKey) {
      setInitialView(sel.candles, INITIAL_VISIBLE[interval]);
      viewKeyRef.current = viewKey;
    }
    if (!intraday) setLoading(false);
  }, [intervals, intradaySets, interval, intraday, coinId, livePrice, applyDataset, setInitialView]);

  // ── Price scale, crosshair and last-value labels in the display currency ───
  useEffect(() => {
    chartRef.current?.applyOptions({
      localization: { priceFormatter: (p: number) => formatMarketPrice(p, currency.code) },
    });
  }, [currency.code]);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Each interval family has its own loading / error state, so an intraday
  // failure never takes 1D and 1W down with it (and vice versa).
  const intraForCoin = intra?.key === key ? intra : null;
  const hourlyUnavailable = interval === 'hourly' && !!intraForCoin?.data && !intraForCoin.data.hourly;
  const shownLoading = intraday ? !intraForCoin || intraForCoin.loading : loading;
  const shownError = intraday
    ? intraForCoin?.error ?? (hourlyUnavailable ? HOURLY_UNAVAILABLE : null)
    : error;
  const retry = () => (intraday ? setIntraRetry((c) => c + 1) : setRetryCount((c) => c + 1));

  const legendUp = legend.close >= legend.open;
  // Candle change (close − open), shown between C and Vol like CoinMarketCap.
  const legendChange    = legend.close - legend.open;
  const legendChangePct = legend.open > 0 ? (legendChange / legend.open) * 100 : 0;

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">

      {/* Lightweight-charts canvas host */}
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ visibility: (shownLoading || shownError) ? 'hidden' : 'visible' }}
      />

      {/* ── OHLC legend overlay ── */}
      {legend.visible && !shownLoading && !shownError && (
        <div
          className="absolute top-3 left-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 sm:gap-3 max-w-[calc(100%-1.5rem)] sm:max-w-none rounded-lg px-3 py-1.5 pointer-events-none z-10 text-[10px] sm:text-[11px] font-mono leading-none"
          style={{
            background: isDark ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <span style={{ color: isDark ? '#666' : '#999' }}>
            O{' '}
            <span style={{ color: legendUp ? '#6db981' : '#c4615e' }}>{fmtP(legend.open, currency.code)}</span>
          </span>
          <span style={{ color: isDark ? '#666' : '#999' }}>
            H{' '}
            <span style={{ color: '#6db981' }}>{fmtP(legend.high, currency.code)}</span>
          </span>
          <span style={{ color: isDark ? '#666' : '#999' }}>
            L{' '}
            <span style={{ color: '#c4615e' }}>{fmtP(legend.low, currency.code)}</span>
          </span>
          <span style={{ color: isDark ? '#666' : '#999' }}>
            C{' '}
            <span style={{ color: legendUp ? '#6db981' : '#c4615e' }}>{fmtP(legend.close, currency.code)}</span>
          </span>
          {legend.isCandle && legend.open > 0 && (
            <span style={{ color: legendUp ? '#6db981' : '#c4615e' }}>
              {fmtP(Math.abs(legendChange), currency.code)}{' '}
              ({legendUp ? '+' : '-'}{Math.abs(legendChangePct).toFixed(2)}%)
            </span>
          )}
          {legend.vol > 0 && (
            <span style={{ color: isDark ? '#666' : '#999' }}>
              Vol{' '}
              <span style={{ color: isDark ? '#aaa' : '#444' }}>{fmtV(legend.vol, currency.code)}</span>
            </span>
          )}
        </div>
      )}

      {/* ── Loading overlay ── */}
      {shownLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
          style={{ background: isDark ? '#161616' : '#ffffff' }}>
          <Loader2 className="w-7 h-7 text-slate-400 animate-spin" />
          <span className="text-sm text-slate-500">Loading chart…</span>
        </div>
      )}

      {/* ── Error overlay ── */}
      {!shownLoading && shownError && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center"
          style={{ background: isDark ? '#161616' : '#ffffff' }}
        >
          <AlertCircle className="w-7 h-7 text-red-400" />
          <p className="text-sm text-slate-400 max-w-xs">{shownError}</p>
          <button
            onClick={retry}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100 bg-white/[0.06] hover:bg-white/[0.1] border border-slate-700 px-4 py-2 rounded-xl transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
